<template>
  <template v-for="(section, i) in sections" :key="i">
    <section
      class="overflow-hidden rounded-theme-md border border-theme-border bg-theme-surface dark:border-theme-border dark:bg-theme-surface"
    >
      <div
        v-if="section.titleKey"
        :class="[
          'border-b px-3 py-2 dark:border-theme-border',
          section.tone === 'primary'
            ? 'border-theme-palette-emerald/40 bg-theme-palette-emerald/10 dark:border-theme-palette-emerald/40 dark:bg-theme-palette-emerald/20'
            : 'border-theme-border bg-theme-bg-subtle dark:bg-theme-surface-elevated',
        ]"
      >
        <h3 class="text-xs font-semibold uppercase tracking-wide text-theme-text-secondary">
          {{ $t(section.titleKey) }}
        </h3>
      </div>
      <div class="space-y-3 px-3 py-3 text-[13px] text-theme-text-secondary">
        <p v-for="(p, pi) in section.paragraphs || []" :key="`p-${pi}`" class="mb-2">
          {{ $t(p) }}
        </p>
        <ul v-if="bulletItems(section).length" class="space-y-1.5">
          <li
            v-for="(item, ii) in bulletItems(section)"
            :key="itemKey(item, ii)"
            class="flex items-start gap-2"
          >
            <span :class="['mt-2 h-1.5 w-1.5 shrink-0 rounded-full', dotClass(item, section)]" />
            <template v-if="typeof item === 'string'">{{ $t(item) }}</template>
            <template v-else>
              <span>
                <span v-if="item.labelKey" class="font-medium text-theme-text">{{
                  $t(item.labelKey)
                }}</span>
                <span v-if="item.labelKey && item.textKey"> - </span>
                <span v-if="item.textKey">{{ $t(item.textKey) }}</span>
                <span v-else-if="item.key">{{ $t(item.key) }}</span>
              </span>
            </template>
          </li>
        </ul>
        <div v-if="faqItems(section).length" class="space-y-3">
          <article
            v-for="(item, qi) in faqItems(section)"
            :key="itemKey(item, qi)"
            class="rounded-md border border-theme-border bg-theme-bg-subtle px-3 py-2 dark:border-theme-border dark:bg-theme-surface-raised"
          >
            <h4 class="text-[13px] font-semibold text-theme-text">
              {{ $t(item.questionKey) }}
            </h4>
            <p class="mt-1 text-[13px] text-theme-text-secondary">
              {{ $t(item.answerKey) }}
            </p>
          </article>
        </div>
      </div>
    </section>
  </template>
</template>

<script setup>
  /* eslint-disable no-undef */
  // Generic, i18n-driven help renderer. `sections` is a declarative descriptor
  // so a tool's help content lives in the registry + locale files rather than
  // in bespoke markup. Each section supports paragraphs, bullets, labeled rows,
  // and FAQ entries.
  defineProps({
    sections: { type: Array, default: () => [] },
  });

  const isFaqItem = (item) => Boolean(item?.questionKey && item?.answerKey);
  const bulletItems = (section) => (section.items || []).filter((item) => !isFaqItem(item));
  const faqItems = (section) => (section.items || []).filter(isFaqItem);
  const itemKey = (item, index) =>
    typeof item === 'string'
      ? item
      : item.key || item.labelKey || item.textKey || item.questionKey || `item-${index}`;
  const dotClass = (item, section) => {
    const tone = typeof item === 'string' ? section.tone : item.tone || section.tone;
    return (
      {
        primary: 'bg-theme-palette-emerald',
        info: 'bg-theme-palette-blue',
        warning: 'bg-theme-palette-amber',
        danger: 'bg-theme-palette-rose',
        muted: 'bg-theme-surface-raised dark:bg-theme-surface-raised',
      }[tone] || 'bg-theme-surface-raised dark:bg-theme-surface-raised'
    );
  };
</script>
